import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import useWebSocketCaller from '../hooks/useWebSocketCaller';
import useQueueHandler from '../hooks/useQueueHandler';
import { useWebSocketContext } from './WebSocketContextProvider';
import { useOperatorContext } from '../comps/login/OperatorContextProvider';
import { createToggleState } from '../comps/avoda/Avoda';

//            Context
// ----------------------------------
const TasksContext = createContext();

//
//            Static  
// ----------------------------------


//            Provider 
// ----------------------------------
export const TasksContextProvider = ({children}) => {
  const {sendMessage}           = useWebSocketCaller();
  const {isWebSocketReady}      = useWebSocketContext();
  const {operator}              = useOperatorContext();
  const {queueData:tasksList}   = useQueueHandler('my-team-tasks');
  const [tasks, setTasks]       = useState([]);
  const [tags, setTags]         = useState();
  const [prios, setPrios]       = useState();

  //------[  Add Task  ]------
  //
  const addTask = useCallback((taskData) => {
    console.log('[TasksContextProvider addTask] [taskData: %s]', JSON.stringify(taskData, null, 2));
    setTasks( prev => [...prev, taskData] );    
  }, []);

  //------[  Delete Task  ]------
  //
  const deleteTask = useCallback((taskData) => {
    console.log('[TasksContextProvider deleteTask] [taskData: %s]', JSON.stringify(taskData, null, 2));
    setTasks( prev => prev.filter(t => t.taskId !== taskData.taskId) );
  }, []);

  //------[  Update Task  ]------
  //
  const updateTask = useCallback((taskData) => {
    console.log('[TasksContextProvider updateTask] [taskData: %s]', JSON.stringify(taskData, null, 2));
    setTasks( prev => prev.map(task => task.taskId === taskData.taskId ? taskData : task));
  }, []);


  //------ Effect ------
  //
  useEffect(()=>{
    if (isWebSocketReady()) {
      sendMessage('get-team-tasks', {
        teamId: operator.selectedTeamId,
        username: operator.username,
        accountId: operator.accountId,
        filterTags: []
      })
    }
  }, [isWebSocketReady, sendMessage, operator?.selectedTeamId, operator?.username, operator?.accountId]);

  //------  Effect  ------
  //
  useEffect(()=>{
    if (tasksList) {
      setTasks(tasksList);
    }
  }, [tasksList]);

  //------  Effect  ------
  //
  // Refresh tags on tasks change
  //
  useEffect(()=>{
    if (tasks) {      
      setTags( prevTags => {
        const newTags = createToggleState([...new Set(tasks.flatMap(task => task.tags))]);        
        // Merge the new tags with the previous state, but remove deleted tags.
        const mergedTags = {...newTags, ...prevTags};
        const newTagsKeys = Object.keys(newTags);
        const withoutDeletedTags = Object.keys(mergedTags).filter(tag => newTagsKeys.includes(tag));
        const filteredTags = withoutDeletedTags.reduce((acc, tag) => {
          acc[tag] = prevTags[tag] || { isSelected: false };
          return acc;
        }, {});
        return filteredTags;
      });
    }    
  }, [tasks]);

  //------  Effect  ------
  //
  // Refresh prios on tasks change
  //
  useEffect(()=>{
    if (tasks) {
      setPrios( prevPrios => {
        const newPrios = createToggleState([...new Set(tasks.map(task => task.priority ? task.priority : 4))], false);
        // Merge the new prios with the previous state, but remove deleted prios.
        const mergedPrios = {...newPrios, ...prevPrios};
        const newPriosKeys = Object.keys(newPrios);
        const withoutDeletedPrios = Object.keys(mergedPrios).filter(prio => newPriosKeys.includes(prio));
        const filteredPrios = withoutDeletedPrios.reduce((acc, prio) => {
          acc[prio] = prevPrios[prio] || { isSelected: false};
          return acc;
        }, {});

        //console.log('[TasksContextProvider setPrios] [filteredPrios: %s]', JSON.stringify(filteredPrios, null, 2));
        return filteredPrios;
      });
    }
  }, [tasks]);


  //------  Memoize  ------
  //
  const memoizedValue = useMemo(()=>({
    tasks, addTask, deleteTask, updateTask, tags, setTags, prios, setPrios
  }),[tasks, addTask, deleteTask, updateTask, tags, setTags, prios, setPrios]);
  
  // Return
  return (<TasksContext.Provider value={memoizedValue}> {children} </TasksContext.Provider>)
};

//            Use Hook
// ----------------------------------
export function useTasksContext() {
  return useContext(TasksContext);
}